import React, { useMemo, useRef } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import PropTypes from 'prop-types';
import CardActions from './CardActions';
import { getGuideColors, GuideHeader, GuideSection, GuideSummary, GuideTips, guideStyles } from './GuideCardLayout';

const capitalize = value => value.charAt(0).toUpperCase() + value.slice(1);

const formatExerciseSpec = exercise => {
  if (exercise.sets && exercise.reps) return `${exercise.sets} × ${exercise.reps}`;
  if (exercise.reps) return exercise.reps;
  if (exercise.sets) return `${exercise.sets} sets`;
  return '';
};

function WorkoutCard({ spec, colors: themeColors, conversationId, messageId }) {
  const captureTargetRef = useRef(null);
  const colors = useMemo(() => getGuideColors(themeColors, 'workout'), [themeColors]);
  const numberedGroups = useMemo(() => {
    let number = 0;
    return spec.exerciseGroups.map(group => ({
      ...group,
      items: group.items.map(item => ({ ...item, number: (number += 1) })),
    }));
  }, [spec]);

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={`Workout: ${spec.title}. ${spec.exerciseCount} exercises.`}
      style={[guideStyles.container, { backgroundColor: colors.background, borderColor: colors.border }]}
    >
      <View ref={captureTargetRef} collapsable={false} dataSet={{ cardCapture: '' }} style={[guideStyles.captureTarget, { backgroundColor: colors.background }]}>
        <GuideHeader kind="Workout" icon="dumbbell" title={spec.title} description={spec.description} metadata={spec.equipment.join(' · ')} colors={colors} />
        <GuideSummary
          items={[
            { label: 'Duration', value: spec.duration },
            { label: 'Level', value: spec.difficulty ? capitalize(spec.difficulty) : '' },
            { label: 'Exercises', value: spec.exerciseCount > 0 ? String(spec.exerciseCount) : '' },
          ]}
          details={[
            { label: 'Rounds', value: spec.rounds },
            { label: 'Burn', value: spec.calories },
          ]}
          colors={colors}
        />

        <GuideSection title="Exercises" colors={colors} />
        {numberedGroups.map((group, groupIndex) => (
          <View key={`group-${groupIndex}`} style={guideStyles.group}>
            {!!group.section && <Text style={[guideStyles.groupTitle, { color: colors.secondary }]}>{group.section}</Text>}
            {group.items.map((exercise, index) => {
              const exerciseSpec = formatExerciseSpec(exercise);
              return (
                <View key={index} style={[styles.exercise, index > 0 && { borderTopWidth: 1, borderColor: colors.border }]}>
                  <View style={[guideStyles.number, { backgroundColor: colors.band }]}>
                    <Text style={[guideStyles.numberText, { color: colors.accent }]}>{exercise.number}</Text>
                  </View>
                  <View style={styles.exerciseBody}>
                    <Text style={[styles.exerciseName, { color: colors.primary }]}>{exercise.name}</Text>
                    {(!!exerciseSpec || !!exercise.rest) && (
                      <View style={styles.exerciseDetails}>
                        {!!exerciseSpec && <Text style={[styles.exerciseSpec, { color: colors.accent }]}>{exerciseSpec}</Text>}
                        {!!exercise.rest && <Text style={[styles.exerciseRest, { color: colors.secondary }]}>Rest {exercise.rest}</Text>}
                      </View>
                    )}
                    {!!exercise.notes && <Text style={[styles.exerciseNotes, { color: colors.secondary }]}>{exercise.notes}</Text>}
                  </View>
                </View>
              );
            })}
          </View>
        ))}
        <GuideTips title="Trainer Tips" tips={spec.tips} colors={colors} />
      </View>

      <CardActions
        conversationId={conversationId}
        messageId={messageId}
        captureTargetRef={captureTargetRef}
        exportTitle={spec.title}
        accentColor={colors.accent}
        borderColor={colors.border}
        bandColor={colors.band}
        textColor={colors.secondary}
      />
    </View>
  );
}

WorkoutCard.propTypes = {
  spec: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
};

const styles = StyleSheet.create({
  exercise: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 13 },
  exerciseBody: { flex: 1, minWidth: 0 },
  exerciseName: { fontSize: 15, fontWeight: '600', lineHeight: 23 },
  exerciseDetails: { flexDirection: 'row', flexWrap: 'wrap', columnGap: 12, rowGap: 4, marginTop: 4 },
  exerciseSpec: { fontSize: 13, fontWeight: '600', lineHeight: 20, flexShrink: 1, fontVariant: ['tabular-nums'] },
  exerciseRest: { fontSize: 13, lineHeight: 20, flexShrink: 1 },
  exerciseNotes: { fontSize: 13, lineHeight: 20, marginTop: 6 },
});

export default React.memo(WorkoutCard);
